import {
  Calendar,
  Globe,
  User,
} from '@digital-twins/design-system/icons'
import i18n from '@/i18n'
import type { ParseKeys, TFunction } from 'i18next'
import type { IconComponent } from '@digital-twins/design-system/icons'
import type {
  IdentityFieldSummary,
  IdentityWorkspaceMode,
} from '@/types/components/audiences/import-session'

const READY_FIELD_STATES = new Set([
  'ready_from_source',
  'ready_from_constant',
  'ready_from_runtime',
  'ready_from_inference',
])

export function isIdentityFieldReady(field: IdentityFieldSummary): boolean {
  return (
    field.satisfied === true ||
    READY_FIELD_STATES.has(field.state) ||
    field.nextAction === 'done'
  )
}

export const FIELD_CONSTANT_OPTIONS: Record<string, string[]> = {
  std_dem_gender: ['Male', 'Female', 'Non-binary', 'Other'],
  std_dem_age: ['Under 18', '18-24', '25-34', '35-44', '45-54', '55-64', '65+'],
}

/** Icons per field (Figma: calendar=Age, user=Gender, globe=Country). */
export const FIELD_ICONS: Record<string, IconComponent> = {
  std_dem_age: Calendar,
  std_dem_gender: User,
  std_geo_country: Globe,
}

const IDENTITY_FIELD_LABEL_KEYS: Record<string, ParseKeys<'app'>> = {
  std_dem_age: 'audienceCreate.identityField.std_dem_age',
  std_dem_gender: 'audienceCreate.identityField.std_dem_gender',
  std_geo_country: 'audienceCreate.identityField.std_geo_country',
}

export function identityFieldLabel(
  canonicalId: string,
  fallback: string | undefined,
  t: TFunction<'app'>,
): string {
  const labelKey = IDENTITY_FIELD_LABEL_KEYS[canonicalId]
  if (labelKey) {
    return t(labelKey)
  }
  return fallback ?? canonicalId
}

export function getModeLabel(mode: IdentityWorkspaceMode) {
  switch (mode) {
    case 'confirm_source':
      return i18n.t('audienceCreate.mapToQuestion', { ns: 'app' })
    case 'set_constant':
      return i18n.t('audienceCreate.setConstantShort', { ns: 'app' })
    case 'review_inference':
      return i18n.t('audienceCreate.customDistribution', { ns: 'app' })
    case 'map_values':
      return i18n.t('audienceCreate.mapValues', { ns: 'app' })
  }
}

export function formatFieldStateLabel(state: IdentityFieldSummary['state']) {
  switch (state) {
    case 'needs_confirmation':
      return i18n.t('audienceCreate.needsConfirmation', { ns: 'app' })
    case 'needs_value_mapping':
      return i18n.t('audienceCreate.needsValueMapping', { ns: 'app' })
    case 'ready_from_source':
    case 'ready_from_constant':
    case 'ready_from_runtime':
    case 'ready_from_inference':
      return i18n.t('audienceCreate.ready', { ns: 'app' })
    default:
      return i18n.t('audienceCreate.needsDefinition', { ns: 'app' })
  }
}

export function getStateBadgeVariant(state: IdentityFieldSummary['state']) {
  switch (state) {
    case 'ready_from_source':
    case 'ready_from_constant':
    case 'ready_from_runtime':
    case 'ready_from_inference':
      return 'positive' as const
    case 'needs_confirmation':
      return 'secondary' as const
    default:
      return 'soft-destructive' as const
  }
}

export function getDefaultWorkspaceMode(
  field: IdentityFieldSummary,
): IdentityWorkspaceMode {
  switch (field.state) {
    case 'needs_confirmation':
    case 'ready_from_source':
      return field.requiresValueMapping ? 'map_values' : 'confirm_source'
    case 'needs_value_mapping':
      return 'map_values'
    case 'ready_from_inference':
      if (field.currentSourceColumn) {
        return field.requiresValueMapping ? 'map_values' : 'confirm_source'
      }
      if (field.nextAction === 'map_values') {
        return 'map_values'
      }
      if (field.nextAction === 'confirm_source') {
        return 'confirm_source'
      }
      return 'review_inference'
    case 'ready_from_constant':
      return 'set_constant'
    case 'ready_from_runtime':
      if (field.currentSourceColumn) {
        return field.requiresValueMapping ? 'map_values' : 'confirm_source'
      }
      if (field.currentConstantValue) {
        return 'set_constant'
      }
      if (field.allowInference) {
        return 'review_inference'
      }
      return 'confirm_source'
  }

  switch (field.nextAction) {
    case 'confirm_source':
      return 'confirm_source'
    case 'map_values':
      return 'map_values'
    case 'review_inference':
      return 'review_inference'
    case 'set_constant':
      return 'set_constant'
    case 'done':
      if (field.requiresValueMapping) return 'map_values'
      if (field.requiresConfirmation) return 'confirm_source'
      if (field.allowInference && !field.satisfied) return 'review_inference'
      return 'set_constant'
    default:
      return 'confirm_source'
  }
}

/**
 * When the shell seeds `confirm_source` but the field is already in the
 * value-mapping phase, open in `map_values` so the user is not stuck on a
 * disabled confirm-source submit. Requires a chosen source column so we do
 * not clamp before a column exists.
 */
export function clampInitialWorkspaceMode(
  field: IdentityFieldSummary | null | undefined,
  mode: IdentityWorkspaceMode | null | undefined,
): IdentityWorkspaceMode | null {
  if (mode == null) {
    return null
  }
  if (mode !== 'confirm_source') {
    return mode
  }
  if (!field || field.requiresConfirmation) {
    return mode
  }
  const inValueMappingPhase =
    field.state === 'needs_value_mapping' ||
    field.nextAction === 'map_values'
  if (!inValueMappingPhase || !field.currentSourceColumn) {
    return mode
  }
  return 'map_values'
}

/**
 * Prefer the mapping flow (Map to question / Map values) over the Custom
 * distribution tab when the field default says mapping — even if the identity
 * shell seeds `review_inference` after async preflight hydrates.
 */
export function resolveInitialWorkspaceMode(
  field: IdentityFieldSummary,
  backendMode: IdentityWorkspaceMode | null | undefined,
): IdentityWorkspaceMode | null {
  const preferred = getDefaultWorkspaceMode(field)
  const fromBackend = backendMode ?? null
  if (
    fromBackend === 'review_inference' &&
    (preferred === 'confirm_source' || preferred === 'map_values')
  ) {
    return clampInitialWorkspaceMode(field, preferred)
  }
  return clampInitialWorkspaceMode(field, fromBackend ?? preferred)
}

/**
 * If the resolved tab is Custom distribution but the field should show mapping
 * first, coerce to the mapping mode so source options / value map load immediately.
 */
export function coerceInferenceTabToMappingDefault(
  field: IdentityFieldSummary,
  mode: IdentityWorkspaceMode | null,
): IdentityWorkspaceMode | null {
  if (mode == null) {
    return null
  }
  if (mode !== 'review_inference') {
    return mode
  }
  const preferred = getDefaultWorkspaceMode(field)
  if (preferred === 'confirm_source' || preferred === 'map_values') {
    return clampInitialWorkspaceMode(field, preferred)
  }
  return mode
}

/**
 * Workspace mode used for rendering and draft hooks. When the user explicitly
 * selects Custom distribution (`review_inference`), that choice is honored even
 * if {@link coerceInferenceTabToMappingDefault} would snap back to Map to question
 * or value mapping for server/fallback-driven `rawCurrentMode`.
 */
export function resolveDisplayedWorkspaceMode(
  field: IdentityFieldSummary | null | undefined,
  rawCurrentMode: IdentityWorkspaceMode | null,
  workspaceMode: IdentityWorkspaceMode | null | undefined,
): IdentityWorkspaceMode | null {
  if (!field || rawCurrentMode == null) {
    return rawCurrentMode ?? null
  }
  if (workspaceMode === 'review_inference') {
    return rawCurrentMode
  }
  return coerceInferenceTabToMappingDefault(field, rawCurrentMode)
}

export function supportsWorkspaceMode(
  field: IdentityFieldSummary,
  mode: IdentityWorkspaceMode | null | undefined,
): mode is IdentityWorkspaceMode {
  if (!mode) return false
  if (mode === 'map_values') {
    return (
      field.requiresValueMapping ||
      field.nextAction === 'map_values' ||
      field.state === 'needs_value_mapping'
    )
  }
  return getWorkspaceModesFromField(field).includes(mode)
}

export function getWorkspaceModesFromField(
  field: IdentityFieldSummary,
): IdentityWorkspaceMode[] {
  const modes: IdentityWorkspaceMode[] = ['confirm_source', 'set_constant']
  if (field.allowInference) {
    modes.push('review_inference')
  }
  // map_values is intentionally excluded: it is a sequential continuation of
  // confirm_source, not an alternative strategy. The tab is never user-selectable;
  // the workspace enters map_values automatically after confirm_source succeeds.
  return modes
}

export function getMutationStatusCopy(
  activeMutation:
    | 'patch_field'
    | 'complete_required_fields'
    | 'refresh_preview'
    | 'create_audience'
    | 'retry_create'
    | 'discard'
    | null
    | undefined,
) {
  switch (activeMutation) {
    case 'patch_field':
      return {
        title: i18n.t('audienceCreate.mutationSavingTitle', { ns: 'app' }),
        description: i18n.t('audienceCreate.mutationSavingBody', { ns: 'app' }),
      }
    case 'complete_required_fields':
      return {
        title: i18n.t('audienceCreate.mutationFinishingTitle', { ns: 'app' }),
        description: i18n.t('audienceCreate.mutationFinishingBody', { ns: 'app' }),
      }
    case 'refresh_preview':
      return {
        title: i18n.t('audienceCreate.mutationRefreshingTitle', { ns: 'app' }),
        description: i18n.t('audienceCreate.mutationRefreshingBody', { ns: 'app' }),
      }
    case 'create_audience':
    case 'retry_create':
      return {
        title: i18n.t('audienceCreate.mutationCreatingTitle', { ns: 'app' }),
        description: i18n.t('audienceCreate.mutationCreatingBody', { ns: 'app' }),
      }
    case 'discard':
      return {
        title: i18n.t('audienceCreate.mutationDiscardingTitle', { ns: 'app' }),
        description: i18n.t('audienceCreate.mutationDiscardingBody', { ns: 'app' }),
      }
    default:
      return null
  }
}
